import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { accountsQueryKey } from "./accounts";
import { isoDate } from "@/dates";

/** A thing that comes round again (`model.md` §3.7). */
export interface RecurringRule {
  id: string;
  name: string;
  account_id: string;
  payee_id?: string;
  interval_unit: "day" | "week" | "month" | "year";
  interval_count: number;
  anchor_day?: number;
  start_date: string;
  end_date?: string;
  /** Signed, in the account's currency: a subscription is negative. */
  template_amount_minor: number;
  template_currency_code: string;
  template_category_id?: string;
  template_is_business: boolean;
  amount_tolerance_bp: number;
  is_active: boolean;
  /** The scheduled job books each occurrence on its due date. */
  auto_book: boolean;
  next_due_date?: string;
}

/**
 * One expected payment.
 *
 * `status` is the whole point of the table: "skip this November" and "this bank
 * row *is* the November charge" are state, and no computation over the rule can
 * hold either (`model.md` §3.7).
 */
export interface Occurrence {
  id: string;
  recurring_rule_id: string;
  due_date: string;
  status: "forecast" | "confirmed" | "skipped";
  expected_amount_minor: number;
  transaction_id?: string;
  matched_at?: string;
  /** manual, import_auto, import_confirmed, or auto_booked (the timer did it). */
  match_method?: string;
}

/**
 * How far back the list reaches for a forecast nobody has dealt with. The
 * server's own auto-booking window (`domain.AutoBookLookbackDays`) is the same
 * length: what it would have booked and did not, a person can still see.
 */
export const OVERDUE_LOOKBACK_DAYS = 35;

interface List<T> {
  items: T[];
}

export const recurringQueryKey = (householdId: string) =>
  ["households", householdId, "recurring"] as const;

export function useRecurringRules(householdId: string | undefined) {
  return useQuery({
    queryKey: [...recurringQueryKey(householdId ?? ""), "rules"],
    queryFn: ({ signal }) =>
      apiFetch<List<RecurringRule>>(`/api/v1/households/${householdId}/recurring-rules`, {
        signal,
      }),
    enabled: Boolean(householdId),
  });
}

export function useUpcoming(householdId: string | undefined, months = 3) {
  const to = new Date();
  to.setMonth(to.getMonth() + months);
  const until = isoDate(to);
  // The API's default window starts today, which made a payment vanish from the
  // list the day it fell due — before anybody could book it.
  const since = new Date();
  since.setDate(since.getDate() - OVERDUE_LOOKBACK_DAYS);
  const from = isoDate(since);

  return useQuery({
    queryKey: [...recurringQueryKey(householdId ?? ""), "upcoming", from, until],
    queryFn: ({ signal }) =>
      apiFetch<List<Occurrence>>(
        `/api/v1/households/${householdId}/recurring-occurrences?from=${from}&to=${until}`,
        { signal },
      ),
    enabled: Boolean(householdId),
  });
}

export interface CreateRuleInput {
  name: string;
  account_id: string;
  payee_id?: string;
  payee_raw?: string;
  interval_unit: string;
  interval_count: number;
  anchor_day?: number;
  start_date: string;
  /** The last day the rule may fall due, inclusive. Absent, it runs until stopped. */
  end_date?: string;
  template_amount_minor: number;
  template_category_id?: string;
  template_is_business?: boolean;
  amount_tolerance_bp?: number;
  /** Absent means off. A PUT replaces the rule, so send it on every edit. */
  auto_book?: boolean;
}

/**
 * A PUT replaces the rule's schedule and template, so it carries every field
 * the rule has — anything left out is cleared, not kept. The account is not
 * among them: it decides the currency, and the server ignores it on update.
 */
export type UpdateRuleInput = Omit<CreateRuleInput, "account_id">;

export function useCreateRule(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRuleInput) =>
      apiFetch<RecurringRule>(`/api/v1/households/${householdId}/recurring-rules`, {
        method: "POST",
        body: input,
      }),
    // Creating a rule materialises its occurrences on the spot (K-23), so the
    // upcoming list is stale the moment this returns.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: recurringQueryKey(householdId) }),
  });
}

/**
 * Changes a rule. The server regenerates its forecasts on the spot and leaves
 * skipped and booked months alone (K-23), so the upcoming list is stale too.
 */
export function useUpdateRule(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRuleInput }) =>
      apiFetch<RecurringRule>(`/api/v1/households/${householdId}/recurring-rules/${id}`, {
        method: "PUT",
        body: input,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: recurringQueryKey(householdId) }),
  });
}

/** Pauses a rule or resumes it: its forecasts go, and come back. */
export function useSetRuleActive(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiFetch<RecurringRule>(
        `/api/v1/households/${householdId}/recurring-rules/${id}/active`,
        { method: "PUT", body: { is_active: active } },
      ),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: recurringQueryKey(householdId) }),
  });
}

/**
 * Deletes a rule. Its forecasts go with it; a month already booked or skipped
 * stays, because it happened.
 */
export function useDeleteRule(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/households/${householdId}/recurring-rules/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: recurringQueryKey(householdId) }),
  });
}

/** Skip, unskip, or book an occurrence. */
export function useOccurrenceAction(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: "skip" | "unskip" | "confirm" }) =>
      apiFetch<Occurrence>(
        `/api/v1/households/${householdId}/recurring-occurrences/${id}/${action}`,
        { method: "POST", body: {} },
      ),
    // Confirming writes a transaction and moves a balance, so those go too.
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: recurringQueryKey(householdId) }),
        client.invalidateQueries({ queryKey: ["households", householdId, "transactions"] }),
        client.invalidateQueries({ queryKey: accountsQueryKey(householdId) }),
      ]),
  });
}
